/* eslint-disable prefer-destructuring */
import { AfterViewChecked, ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, OnDestroy, OnInit, signal, viewChild, ViewContainerRef, WritableSignal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { I18n, NgtButtonComponent, NgtLinkComponent, TranslatePipe } from '@corporate/ng-tebis-base-components';
import { PortalModule } from '@angular/cdk/portal';
import { globalDraggedTab, WindowTabsComponent } from './window-tabs/window-tabs.component';
import { MatMenuModule } from '@angular/material/menu';
import { MdiLayoutService } from './mdi-layout.service';
import { AuthService } from '../../01_core/auth/auth.service';
import { version } from '../../../../../../package.json';
import { environment } from '../../../environments/environment';
import { WindowStore } from './window-tabs/window.store';
import { FeatureInstanceComponent } from '../feature-instance/feature-instance.component';
import { WindowedRouterLinkComponent } from '../../01_core/windowed-router-link/windowed-router-link.component';
import { WindowContextComponent } from './window-context/window-context.component';
import { CopyContextLinkComponent } from './copy-context-link/copy-context-link.component';
import { CdkDragDrop } from '@angular/cdk/drag-drop'; // , CdkDropList
import { TestingService } from '../../testing.service';
import { CustomerNavbarComponent } from '../../06_component/customer/customer-navbar/customer-navbar.component';
import { ArticleNavbarComponent } from '../../06_component/article/article-navbar/article-navbar.component';
import { ManufacturingOrderNavbarComponent } from '../../06_component/manufacturing-order/manufacturing-order-navbar/manufacturing-order-navbar.component';
import { NavbarButtonComponent } from "../../06_component/navbars/navbar-button/navbar-button.component";
import { RouterOutlet } from "@angular/router";

@Component({
  selector: 'tps-mdi-layout',
  imports: [
    MatIconModule,
    NgtButtonComponent,
    MatMenuModule,
    TranslatePipe,
    PortalModule,
    WindowTabsComponent,
    WindowedRouterLinkComponent,
    FeatureInstanceComponent,
    WindowContextComponent,
    CopyContextLinkComponent,
    // CdkDropList,
    CustomerNavbarComponent,
    ArticleNavbarComponent,
    ManufacturingOrderNavbarComponent,
    NavbarButtonComponent,
    NgtLinkComponent,
    RouterOutlet
],
  templateUrl: './mdi-layout.component.html',
  styleUrls: ['./mdi-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MdiLayoutComponent implements AfterViewChecked, OnInit, OnDestroy {

  i18n = inject(I18n);
  authService = inject(AuthService);
  state = inject(MdiLayoutService);
  testing = inject(TestingService);

  windowStore = inject(WindowStore);
  windows = computed(() => this.windowStore.windows().map(window => ({...window, url: window.tabs[window.activeTabIndex]?.url})));
  windowCount = computed(() => this.windows().length);
  tabCountWindow1 = computed(() => this.windows()[0].tabs.length);
  activeTabsContext = computed(() => this.windows().map(w => w.tabs[w.activeTabIndex].url));

  mainWindow = computed(() => this.windows()[0]);
  additionalWindows = computed(() => this.windows().slice(1));
  windowTabsDropListIDs = computed(() => this.windows().map((_, i) => `window-tabs-drop-list-${i}`));
  windowZoneDropListIDs = computed(() => this.windows().flatMap((_, i) => [
    `window-zone-before-${i}`,
    `window-zone-inside-${i}`,
    `window-zone-after-${i}`,
  ]));
  allDropListIDs = computed(() => [...this.windowTabsDropListIDs(), ...this.windowZoneDropListIDs()]);

  limitedTabBar = signal(0);
  tabBarLimit = signal(0);
  tabBarLimits = computed(() => this.windows().map((w, i) => {
    if (i === this.limitedTabBar()) {
      if (this.tabBarLimit() > 0) {
        return Math.max(200, this.tabBarLimit());
      }
    }
    return 0;
  }));
  updateInterval: any = null;

  scrollSnapping = signal(true);
  scrollable = signal(false);
  mainContent = viewChild<ElementRef<HTMLElement>>('mainContent');

  displayedUserName = computed(() => {
    return this.authService.givenName() + ' ' + this.authService.familyName();
  });

  // show version with environment name
  versionInfo = `TebiProc Frontend ${version}-${environment.name}`;

  globalDraggedTab = globalDraggedTab;
  lastGlobalDraggedTab: {windowIndex: number, tabIndex: number}|null = null;

  route = computed(() => {
    const window = this.windowStore.windows()[this.windowStore.activeWindowIndex()];
    return window.tabs[window.activeTabIndex].url;
  });

  toggle(booleanSignal: WritableSignal<boolean>) {
    booleanSignal.update(active => !active);
  }

  viewContainerRef = inject(ViewContainerRef);

  constructor() {
    this.i18n.addNamespace('windowtabs');
    this.i18n.addNamespace('cam-master-db');

    effect(() => {
      if (globalDraggedTab()) {
        this.lastGlobalDraggedTab = globalDraggedTab();
      }
    });
  }

  ngOnInit(): void {
    this.updateInterval = setInterval(() => {
      const toolCorner = document.getElementById('tool-corner');
      const toolbars = this.windows().map((window, i) => document.getElementById(`window-toolbar-${i}`)).filter(Boolean);
      this.tabBarLimit.set(0);
      if (toolCorner && this.windows().length === toolbars.length) {
        const scrollOffset = this.mainContent()?.nativeElement.scrollLeft || 0;
        for (let i = 0; i < toolbars.length; i++) {
          const toolbar = toolbars[i]!;
          if (toolCorner.offsetLeft < toolbar.offsetLeft - scrollOffset + toolbar.offsetWidth) {
            this.limitedTabBar.set(i);
            this.tabBarLimit.set(toolCorner.offsetLeft - (toolbar.offsetLeft - scrollOffset));
            break;
          }
        }
      }
    }, 200);
  }

  ngOnDestroy(): void {
    if (this.updateInterval !== null) {
      clearInterval(this.updateInterval);
    }
  }

  ngAfterViewChecked(): void {
    const container = this.mainContent()?.nativeElement;
    if (!container) {
      this.scrollable.set(false);
      return;
    }
    this.scrollable.set(container.scrollWidth > container.clientWidth);
  }

  logout() {
    this.authService.logout();
  }

  getUrlComputed(windowIndex: number) {
    return computed(() => {
      const window = this.windows()[windowIndex];
      return window.tabs[window.activeTabIndex].url;
    });
  }

  getTabIndex(windowIndex: number) {
    return computed(() => this.windows()[windowIndex].activeTabIndex);
  }

  addCompareModeWindow() {
    const currentTab = this.windowStore.activeTab();
    this.windowStore.openWindow(currentTab.url, currentTab.label, currentTab.fallbackLabel, currentTab.type);
    this.windowStore.closeTab(0, currentTab.id);
  }

  removeCompareModeWindow() {
    const tabs = this.windowStore.windows()[1].tabs;
    this.windowStore.closeWindow(1);
    tabs.forEach(tab => this.windowStore.newTab(tab.url, tab.type, tab.label, tab.fallbackLabel));
  }

  onDrop(event: CdkDragDrop<void>, windowIndex: number, targetPosition: 'before'|'inside'|'after') {
    const draggedTab = this.lastGlobalDraggedTab;
    console.log('Drop event:', {event, windowIndex, targetPosition, draggedTab});
    if (!draggedTab) {
      return;
    }
    if (targetPosition === 'inside') {
      this.windowStore.transferTab(draggedTab.windowIndex, draggedTab.tabIndex, windowIndex, this.windowStore.windows()[windowIndex].tabs.length);
      return;
    }
    const tab = this.windowStore.windows()[draggedTab.windowIndex].tabs[draggedTab.tabIndex];
    this.windowStore.closeTab(draggedTab.windowIndex, this.windowStore.windows()[draggedTab.windowIndex].tabs[draggedTab.tabIndex].id);
    this.windowStore.openWindow(tab.url, tab.label, tab.fallbackLabel, tab.type, windowIndex + (targetPosition === 'after' ? 1 : 0));
  }

  stringSignal(s: string) {
    return signal(s);
  }
}
